import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { msg, Trans } from '@lingui/macro';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem, Select, styled, TextField, useTheme } from
'@mui/material/';
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  gridClasses } from
'@mui/x-data-grid';
// import AddIcon from '@mui/icons-material/Add'
// import AddBoxIcon from '@mui/icons-material/AddBox'
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OrganizationIcon from '../OrganizationIcon';
import { randStr } from '../../utils';
import SvgCustomIcon from "../icons/SvgCustomIcon";
import { Typography } from "@mui/material";
import { StripedDataGrid } from "./StripedDataGrid";
import { Check } from "@mui/icons-material";
import { resolveConfigDisplayLabel } from '../../utils/config-localization';
import { resolveGridSectionHeaderLabel } from '../../utils/grid-section-header.helper';
import { getInitFromStorage } from '../../offline/session-store';
import ConfigLabelsDialog from '../admin/config-labels-dialog';
import ConfigOptionIconsDialog from '../admin/config-option-icons-dialog';
import TranslateIcon from '@mui/icons-material/Translate';
import ImageIcon from '@mui/icons-material/Image';
import {
  formatConfigSelectOptionList,
  parseConfigSelectOptionList,
} from '../../utils/config-select-option-list';
import { getBaseOrganizationsForConfig } from '../../utils/config-base-organizations.util';

const CONFIG_FIELD_TYPE_OPTIONS = ['text', 'select', 'date', 'datetime'];

// canChange: false
// column: "report_date"
// isRequired: true
// label: "Report date"
// margin: "dense"
// options: []
// permissions: [1, 2, 3]
// type: "date"
// unit: ""
// value: {}

const paginationModel = { page: 0, pageSize: 25 };

/**
 * Icon for form-config Organizations column from API `basicOrganization` (or `basic_id`).
 * 1 = police, 2 = healthcare, 3 = insurance; unknown → insurance (same as legacy default).
 */
function getIconNameFromBasicOrganization(basicOrganization) {
  const b = Number(basicOrganization);
  if (b === 1) return 'police';
  if (b === 2) return 'healthCrossOutline';
  if (b === 3) return 'insurance';
  return 'insurance';
}

function getNextOptionValue(options) {
  const numericValues = (options || [])
  .map((opt) => Number(opt.value))
  .filter((value) => Number.isFinite(value));
  if (!numericValues.length) return 0;
  return Math.max(...numericValues) + 1;
}

export default function ConfTable({ section, sections, sectionRows, modifyField, error, message, handleResetMessage }) {
  const location = useLocation();
  const init = location.state?.init?.configuration ? location.state.init : getInitFromStorage();
  const { configuration, organizations } = init;
  const baseOrganizations = getBaseOrganizationsForConfig(organizations);
  const theme = useTheme();
  const { _ } = useLingui();
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [deleteDialogId, setDeleteDialogId] = useState(null);
  const [translationsDialogRowId, setTranslationsDialogRowId] = useState(null);
  const [iconsDialogRowId, setIconsDialogRowId] = useState(null);

  useEffect(() => {
    if (sectionRows.length) setRows(sectionRows);
  }, [sectionRows]);

  useEffect(() => {

  }, [rows]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        handleResetMessage();
      }, 3000); // Nasconde il messaggio dopo 3 secondi

      return () => clearTimeout(timer); // Pulisce il timeout se il componente si smonta o message cambia
    }
  }, [message, handleResetMessage]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  // const CustomCellEditor = props => {
  //   const { id, field, value, row, api } = props

  //   const handleChange = event => {
  //     const newTypeValue = event.target.value
  //     let value
  //     if (newTypeValue === 'date') value = _(msg`date of fill`)
  //     if (newTypeValue === 'datetime') value = _(msg`date & time of fill`)
  //     if (newTypeValue === 'text') value = _(msg`empty field`)
  //     if (newTypeValue === 'select') value = _(msg`Unknown`)

  //     // Update the row data in the DataGrid
  //     // api.setEditCellValue({ id, field, value: newValue })

  //     // Recalculate the total
  //     const updatedRow = { ...row, [field]: newTypeValue, value }

  //     // Update the row in the DataGrid
  //     api.updateRows([updatedRow])
  //   }

  //   return (
  //     <TextField
  //       select
  //       value={value}
  //       onChange={handleChange}
  //       fullWidth
  //       autoFocus
  //     >
  //       {['text', 'select', 'date', 'datetime'].map(opt => (
  //         <MenuItem key={opt} value={opt}>
  //           {opt}
  //         </MenuItem>
  //       ))}
  //     </TextField>
  //   )
  // }

  const handleDeleteClick = (id) => () => {
    setDeleteDialogId(id);
  };

  const handleConfirmDelete = () => {
    const id = deleteDialogId;
    setDeleteDialogId(null);
    handleResetMessage();
    setRows(rows.filter((row) => row.id !== id));
    const row = rows.find((row) => row.id === id);
    modifyField(row.label, { action: 'delete', section: row.section });
  };

  const handleAddClick = () => {
    handleResetMessage();
    const id = randStr();
    setRows((oldConfig) => [
    {
      id,
      section,
      permissions: [],
      label: '',
      isRequired: false,
      type: 'text',
      options: [],
      unit: '',
      value: '',
      tooltip: '',
      isMasked: false,
      allowFutureDates: false,
      canChange: true,
      isNew: true,
      labels: {}
    },
    ...oldConfig]
    );
    setRowModesModel((oldModel) => ({
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'permissions' },
      ...oldModel
    }));
  };

  const handleEditClick = (id) => () => {
    handleResetMessage();
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow, oldRow) => {
    const payload = { ...newRow };
    payload.permissions = Array.isArray(payload.permissions) ?
    payload.permissions.filter((p) => p != null) :
    [];
    if (payload.type === 'date' || payload.type === 'datetime') {
      payload.allowFutureDates = Boolean(payload.allowFutureDates);
    } else {
      payload.allowFutureDates = false;
    }
    if (payload.type === 'text' && (payload.value == null || payload.value === '' || payload.value === _(msg`empty field`))) {
      payload.value = '';
    }
    setRows(rows.map((row) => row.id === payload.id ? payload : row));
    const label = payload.isNew ? payload.label : oldRow.label;
    modifyField(label, payload);
    return payload;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const getOriginalOptionsForRow = (row) =>
    configuration?.[row.section]?.elements
    ?.find((elem) => elem.column === row.column || elem.label === row.label)
    ?.options ?? row.options ?? [];

  const columns = [
  { field: 'id', headerName: '' } // hidden
  , { field: 'section', headerName: _(msg`Section`) } // hidden
  , { field: 'column', headerName: _(msg`Column`) } // hidden
  , { field: 'isNew', headerName: _(msg`New?`), type: 'boolean' } // hidden
  , {
    field: 'permissions',
    headerName: _(msg`Organizations`),
    headerClassName: 'overload-header',
    flex: 1.1 // , minWidth: 110
    , editable: (_, row) => row.canChange || row.isNew,
    valueGetter: (value) => value?.map((v) => organizations.find((o) => o.value === v)?.label),
    valueSetter: (value, row) => {
      if (value) {
        let matches = [];

        if (Array.isArray(value)) matches = value;else
        {
          const regex = /\b(police|healthcare|insurance)\b/gi;
          let match;

          while ((match = regex.exec(value)) !== null) {
            matches = [...matches, match[0]];
          }
        }

        // const newValue = matches.map(v => organizations.find(o => o.label === v).value)
        const newValue = matches.
        map((v) => {
          const organization = organizations.find((o) => o.label === v);
          return organization ? organization.value : null;
        }).
        filter((v) => v != null);
        return { ...row, permissions: newValue };
      }
      return {};
    },
    renderCell: (params) => {
      const { row } = params;
      const isInEditMode = rowModesModel[row.id]?.mode === GridRowModes.Edit;

      // Se è in modalità edit E (canChange è true O è una nuova riga), mostra una select
      if (isInEditMode && (row.canChange || row.isNew)) {
        // Usa row.permissions direttamente (valori numerici) invece di params.value (label)
        const currentValues = row.permissions || [];
        return (
          <Select
            multiple
            value={currentValues}
            onChange={(e) => {
              const selectedValues = e.target.value;
              // Aggiorna immediatamente lo stato per vedere il cambiamento a vista
              const updatedRows = rows.map((r) =>
              r.id === row.id ? { ...r, permissions: selectedValues } : r
              );
              setRows(updatedRows);
            }}
            size="small"
            fullWidth
            autoFocus
            sx={{
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { padding: '8px 14px', minHeight: 'auto' }
            }}
            renderValue={(selected) => {
              if (selected.length === 0) return '';
              return selected.map((val) => {
                const org = organizations.find((o) => o.value === val);
                return org ? org.label : val;
              }).join(', ');
            }}>
            
                            {baseOrganizations.map((org) =>
            <MenuItem key={org.value} value={org.value}>
                                    {org.label}
                                </MenuItem>
            )}
                        </Select>);

      }

      // Visualizzazione icone: usa basicOrganization (o basic_id) dell'org, non le label i18n
      const permissionIds = row.permissions || [];
      if (permissionIds.length) {
        return (
          <Box sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            height: '100%'
          }}>
                            {permissionIds.map((permId) => {
              const org = organizations.find((o) => o.value === permId);
              const basic = org?.basicOrganization ?? org?.basic_id;
              const iconName = getIconNameFromBasicOrganization(basic);
              return (
                <SvgCustomIcon key={permId} color={theme.palette.newColors.gray} name={iconName} />);

            })}
                        </Box>);

      }

      // Se non ci sono organizzazioni e non è in edit mode, mostra nulla o un placeholder
      return null;
    }
  },
  {
    field: 'label',
    headerName: _(msg`Label`),
    headerClassName: 'overload-header',
    flex: 2 // , minWidth: 200
    , editable: (_, row) => row.canChange || row.isNew,
    valueGetter: (value, row) => resolveConfigDisplayLabel(row) || value,
    // , renderEditCell  : ({ value }) => {
    // if (value === '') {
    //   return <TextField sx={{ border: null, width: '100%', '&:focus-visible': { border: 0 }, '&:active': { border: 0 } }} placeholder='Type a label...' />
    // }
    // return value
    // }
  },
  {
    field: 'isRequired',
    headerName: _(msg`Required`),
    headerClassName: 'overload-header',
    type: 'boolean',
    flex: 1 // , minWidth: 100
    , editable: (_, row) => row.canChange || row.isNew,
    renderCell: (params) => {
      const isVerified = params.value;
      const color = theme.palette.mode === 'dark' ?
      isVerified ? '#4caf50' : '#f44336' // verde / rosso in dark
      : isVerified ? '#2e7d32' : '#d32f2f'; // verde / rosso in light

      return isVerified ?
      <Check sx={{ color }} /> :
      <CancelIcon sx={{ color }} />;
    }
  },
  {
    field: 'isMasked',
    headerName: _(msg`Sensitive`),
    headerClassName: 'overload-header',
    type: 'boolean',
    flex: 1 // , minWidth: 100
    // System fields (canChange: false) may still toggle Sensitive; API already persists IsMasked.
    , editable: true,
    renderCell: (params) => {
      const isMasked = params.value;
      const color = theme.palette.mode === 'dark' ?
      isMasked ? '#4caf50' : '#f44336' // verde / rosso in dark
      : isMasked ? '#2e7d32' : '#d32f2f'; // verde / rosso in light

      return isMasked ?
      <Check sx={{ color }} /> :
      <CancelIcon sx={{ color }} />;
    }
  },
  {
    field: 'allowFutureDates',
    headerName: _(msg`Future dates`),
    headerClassName: 'overload-header',
    type: 'boolean',
    flex: 1,
    editable: (_, row) => row.type === 'date' || row.type === 'datetime',
    renderCell: (params) => {
      if (params.row.type !== 'date' && params.row.type !== 'datetime') {
        return null
      }
      const enabled = params.value
      const color = theme.palette.mode === 'dark' ?
      enabled ? '#4caf50' : '#f44336'
      : enabled ? '#2e7d32' : '#d32f2f'

      return enabled ?
      <Check sx={{ color }} /> :
      <CancelIcon sx={{ color }} />;
    }
  },
  {
    field: 'type',
    headerName: _(msg`Type`),
    headerClassName: 'overload-header',
    flex: 1 // , minWidth: 100
    , editable: (_, row) => row.canChange || row.isNew,
    renderEditCell: (params) => {
      const { id, value } = params;
      return (
        <Select
          value={value || 'text'}
          onChange={(event) => {
            params.api.setEditCellValue({
              id,
              field: 'type',
              value: event.target.value,
            });
          }}
          size="small"
          fullWidth
          autoFocus
          sx={{
            border: 'none',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-select': { padding: '8px 14px' },
          }}
        >
          {CONFIG_FIELD_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt === 'text' && _(msg`text`)}
              {opt === 'select' && _(msg`select`)}
              {opt === 'date' && _(msg`date`)}
              {opt === 'datetime' && _(msg`datetime`)}
            </MenuItem>
          ))}
        </Select>
      );
    },
    renderCell: (params) => {
      const labelMap = {
        text: _(msg`text`),
        select: _(msg`select`),
        date: _(msg`date`),
        datetime: _(msg`datetime`),
      };
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {labelMap[params.value] ?? params.value ?? labelMap.text}
        </Box>
      );
    },
  },
  {
    field: 'options',
    headerName: _(msg`Options`),
    headerClassName: 'overload-header',
    flex: 4 // , minWidth: 400
    , editable: (_, row) => row.type === 'select' && (row.canChange || row.isNew),
    valueGetter: (value, row) =>
      row.type === 'select' ? formatConfigSelectOptionList(value || []) : '',
    valueSetter: (value, row) => {// ctx additional parameter
      if (row.type === 'select') {
        const existingOptions = getOriginalOptionsForRow(row);
        let nextValue = getNextOptionValue(existingOptions);
        const input = parseConfigSelectOptionList(value);
        const newOptions = (input.length ? input : [{ label: 'Unknown' }]).map((inputOption, index) => {
          const existingOption =
            existingOptions.find((option) => String(option.value) === String(inputOption.value)) ||
            (row.options || []).find((option) => String(option.value) === String(inputOption.value)) ||
            existingOptions[index] ||
            (row.options || [])[index] ||
            existingOptions.find((option) => option.label === inputOption.label) ||
            (row.options || []).find((option) => option.label === inputOption.label);

          if (existingOption) {
            return {
              ...existingOption,
              label: inputOption.label,
              icon: existingOption.icon ?? '',
              flag: existingOption.flag ?? '',
            };
          }

          const createdOption = {
            label: inputOption.label,
            value: nextValue,
            labels: {},
            icon: '',
            flag: '',
          };
          nextValue += 1;
          return createdOption;
        });
        return { ...row, options: newOptions };
      }
      return { ...row, options: [] };
    }
  },
  {
    field: 'unit',
    headerName: _(msg`Unit`),
    headerClassName: 'overload-header',
    flex: 0.75 // , minWidth        : 75
    , editable: (_, row) => row.canChange || row.isNew
  },
  {
    field: 'tooltip',
    headerName: _(msg`Tooltip`),
    headerClassName: 'overload-header',
    flex: 2 // , minWidth: 200
    , editable: true // Sempre editabile
  },
  {
    field: 'value',
    headerName: _(msg`Default`),
    headerClassName: 'overload-header',
    flex: 1 // , minWidth        : 100
    , editable: (_, row) => row.isNew,
    valueGetter: (value, row) => {
      if (row.type === 'date') return _(msg`date of fill`);
      if (row.type === 'datetime') return _(msg`date & time of fill`);
      if (row.type === 'text') return _(msg`empty field`);
      if (row.type === 'select') return _(msg`Unknown`);
      return value;
    }
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: _(msg`Actions`),
    headerClassName: 'overload-header',
    cellClassName: 'actions',
    width: 176,
    minWidth: 176,
    maxWidth: 176,
    flex: 0,
    resizable: false,
    align: 'center',
    headerAlign: 'center',
    getActions: (obj) => {
      const { id, row } = obj;
      const { canChange } = row;
      const isSelectField = row.type === 'select' && (row.options?.length ?? 0) > 0;
      const isInEditMode = () => {
        return rowModesModel[id]?.mode === GridRowModes.Edit;
      };

      if (isInEditMode()) {
        return [
        <GridActionsCellItem
          icon={<SvgCustomIcon name={'save'} />}
          label={_(msg`Save`)}
          sx={{
            color: 'primary.main'
          }}
          onClick={handleSaveClick(id)} />,

        <GridActionsCellItem
          icon={<SvgCustomIcon name={'cancel'} />}
          label={_(msg`Cancel`)}
          className="textPrimary"
          onClick={handleCancelClick(id)}
          color="inherit" />];


      }

      return [
      <GridActionsCellItem
        icon={<TranslateIcon sx={{ color: theme.palette.newColors.gray }} />}
        label={_(msg`Translations`)}
        onClick={() => setTranslationsDialogRowId(id)}
        color="inherit" />,

      <GridActionsCellItem
        icon={
          <ImageIcon
            sx={{
              color: isSelectField
                ? theme.palette.newColors.gray
                : theme.palette.action.disabled,
            }}
          />
        }
        label={_(msg`Option icons`)}
        onClick={() => setIconsDialogRowId(id)}
        disabled={!isSelectField}
        color="inherit" />,

      <GridActionsCellItem
        icon={<SvgCustomIcon color={theme.palette.newColors.gray} name={'edit'} />}
        label={_(msg`Edit`)}
        className="textPrimary"
        onClick={handleEditClick(id)}
        color="inherit" />,

      <GridActionsCellItem
        icon={<SvgCustomIcon color={!canChange ? theme.palette.action.disabled : theme.palette.newColors.gray} name={'delete'} />}
        label={_(msg`Delete`)}
        onClick={handleDeleteClick(id)}
        color="inherit"
        disabled={!canChange} />];


    }
  }];


  const legendWithIcon = [
  { label: _(msg`Police`), value: 'Police' },
  { label: _(msg`Healthcare`), value: 'Healthcare' },
  { label: _(msg`Insurance`), value: 'Insurance' }];


  return (
    <Box
      width='100%'
      sx={{
        '& .overload-header': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        },
        '& .MuiDataGrid-cell.actions': {
          overflow: 'visible',
          justifyContent: 'center',
        },
        '& .MuiDataGrid-cell.actions .MuiDataGrid-actionsCell': {
          width: '100%',
          justifyContent: 'center',
          gap: 0,
        },
      }}>
      
            <Dialog open={Boolean(deleteDialogId)} onClose={() => setDeleteDialogId(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: theme.palette.newColors?.gray || theme.palette.text.primary }}>
                    {_(msg`Delete field`)}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {_(msg`Are you sure you want to delete this field? This action cannot be undone.`)}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
            onClick={() => setDeleteDialogId(null)}
            sx={{ color: theme.palette.newColors?.gray || theme.palette.text.secondary }}>
            
                        {_(msg`Cancel`)}
                    </Button>
                    <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error">
            
                        {_(msg`Delete`)}
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfigLabelsDialog
              open={Boolean(translationsDialogRowId)}
              onClose={() => setTranslationsDialogRowId(null)}
              canonicalLabel={rows.find((row) => row.id === translationsDialogRowId)?.label ?? ''}
              fieldType={rows.find((row) => row.id === translationsDialogRowId)?.type}
              labels={rows.find((row) => row.id === translationsDialogRowId)?.labels}
              options={rows.find((row) => row.id === translationsDialogRowId)?.options ?? []}
              onSave={({ labels, options }) => {
                const target = rows.find((row) => row.id === translationsDialogRowId)
                if (!target) return
                const updatedRow = {
                  ...target,
                  labels,
                  ...(options != null ? { options } : {}),
                }
                setRows((prev) => prev.map((row) => (row.id === target.id ? updatedRow : row)))
                const canonical = target.isNew ? updatedRow.label : target.label
                modifyField(canonical, updatedRow)
              }}
            />
            <ConfigOptionIconsDialog
              open={Boolean(iconsDialogRowId)}
              onClose={() => setIconsDialogRowId(null)}
              canonicalLabel={rows.find((row) => row.id === iconsDialogRowId)?.label ?? ''}
              options={rows.find((row) => row.id === iconsDialogRowId)?.options ?? []}
              onSave={({ options }) => {
                const target = rows.find((row) => row.id === iconsDialogRowId)
                if (!target) return
                const updatedRow = { ...target, options }
                setRows((prev) => prev.map((row) => (row.id === target.id ? updatedRow : row)))
                const canonical = target.isNew ? updatedRow.label : target.label
                modifyField(canonical, updatedRow)
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', p: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                    <Typography sx={{
            color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
            fontWeight: 'bold'
          }}>{resolveGridSectionHeaderLabel(section, configuration, _)}</Typography>
                    <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            onClick={handleAddClick}
            sx={{
              background: theme.palette.newColors.pinkGradient,
              color: 'white',
              '&:hover': {
                background: theme.palette.newColors.pinkGradient,
                opacity: 0.9
              }
            }}>
            
                        <Trans>Add item</Trans>
                    </Button>
                </Box>
                {section === 'crash' &&
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                        {legendWithIcon.map((v, k) => {
            const iconName = v.value === 'Police' ? 'police' : v.value === 'Healthcare' ? 'healthCrossOutline' : 'insurance';
            const iconColor = theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightgray;
            const typoColor = theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightgray;
            return (
              <Box key={k} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                    <SvgCustomIcon color={iconColor} name={iconName} />
                                    <Typography sx={{ color: typoColor }}>{v.label}</Typography>

                                </Box>);

          })}
                    </Box>
        }
            </Box>
            <StripedDataGrid
        showColumnVerticalBorder
        showCellVerticalBorder
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={() => handleRowModesModelChange('crash')}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        isCellEditable={(params) => {
          const { row, field } = params;

          // Tooltip è sempre editabile
          if (field === 'tooltip') return true;

          // Permissions è sempre gestito da renderCell (mai dal default edit input)
          if (field === 'permissions') return false;

          // Se è una nuova riga, tutto il resto è editabile
          if (row.isNew) return true;

          // Value editabile solo per nuove righe
          if (field === 'value') return false;

          // Options editabile solo se type è 'select' e canChange è true
          if (field === 'options') {
            return row.type === 'select' && row.canChange;
          }

          // Sensitive editabile anche su campi di sistema (canChange: false)
          if (field === 'isMasked') {
            return true;
          }

          // Future dates: solo campi date/datetime
          if (field === 'allowFutureDates') {
            return row.type === 'date' || row.type === 'datetime';
          }

          // Label, isRequired, unit editabili solo se canChange è true
          if (field === 'label' || field === 'isRequired' || field === 'unit') {
            return row.canChange;
          }

          // Default: non editabile
          return false;
        }}
        initialState={{
          pagination: { paginationModel },
          columns: {
            columnVisibilityModel: {
              id: false,
              section: false,
              column: false,
              isNew: false,
              value: false
            }
          }
        }}
        experimentalFeatures={{ newEditingApi: true }} // Enable the new editing API
        hideFooter={rows.length <= paginationModel.pageSize}
        pageSizeOptions={[paginationModel.pageSize]}
        getRowClassName={(params) => {
          return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
        }} />
      
            {error && <Alert sx={{ mt: 2 }} severity="error">
                <Trans>There was an error: {error}.</Trans>
            </Alert>}
            {message && <Alert sx={{ mt: 2 }} severity='success'>
                {message}
            </Alert>}
        </Box>);

}